/**
 * Tour step definitions for every major page/tab in CountBucks.
 * Each tour is an array of steps: { selector, title, content, position }
 *
 * Selectors are kept resilient — when a `data-testid` may not be present on
 * mobile layouts (e.g., the cfo-tabs row collapses), provide a comma-separated
 * fallback selector so the tour driver can still anchor.
 */

export const tours = {
  dashboard: {
    id: 'dashboard',
    title: 'Dashboard Tour',
    description: 'Learn how your financial command center works',
    icon: 'layout-dashboard',
    category: 'Overview',
    steps: [
      { selector: '[data-testid="dashboard-v2"]', title: 'Your Dashboard', content: 'This is your financial command center. It shows real-time widgets with key metrics about your business health.', position: 'bottom' },
      { selector: '[data-testid="quick-create-bar"]', title: 'Quick Create Bar', content: 'Quickly create invoices, bills, or journal entries without navigating away from the dashboard.', position: 'bottom' },
      { selector: '[data-testid="onboarding-checklist"]', title: 'Getting Started Checklist', content: 'This checklist tracks your setup progress. Complete each step to get your workspace fully configured.', position: 'bottom' },
    ]
  },

  invoices: {
    id: 'invoices',
    title: 'Invoices Tour',
    description: 'Create, send, and track invoices from your clients',
    icon: 'file-text',
    category: 'Receivables',
    steps: [
      { selector: '[data-testid="invoices-page"]', title: 'Invoices Page', content: 'This is where you manage all your invoices — create new ones, track payments, and monitor what clients owe you.', position: 'bottom' },
      { selector: '[data-testid="new-invoice-btn"]', title: 'Create an Invoice', content: 'Click here to create a new invoice. You\'ll add a client, line items, payment terms, and tax rates.', position: 'bottom' },
      { selector: '[data-testid="help-panel-trigger"]', title: 'Need Help?', content: 'Click this question mark icon on any page for detailed help content, tips, and explanations.', position: 'left' },
    ]
  },

  bills: {
    id: 'bills',
    title: 'Bills Tour',
    description: 'Record and track bills you owe to vendors',
    icon: 'receipt',
    category: 'Payables',
    steps: [
      { selector: '[data-testid="bills-page"]', title: 'Bills Page', content: 'Track everything you owe to vendors and suppliers. Record bills as you receive them to stay on top of payments.', position: 'bottom' },
      { selector: '[data-testid="new-bill-btn"]', title: 'Record a Bill', content: 'Click here to record a new bill from a vendor. Enter the vendor, amount, due date, and expense category.', position: 'bottom' },
      { selector: '[data-testid="bills-page"]', title: 'Attach a Receipt', content: 'Drag a PDF or photo onto any bill to keep an audit-ready paper trail. Bonus: enable AI OCR to skip manual data entry.', position: 'bottom' },
      { selector: '[data-testid="bills-page"]', title: 'Mark as Paid', content: 'When you pay a bill, mark it paid to close the loop. This updates your AP Aging report and your cash balance in real time.', position: 'bottom' },
    ]
  },

  journal_entries: {
    id: 'journal_entries',
    title: 'Journal Entries Tour',
    description: 'Record transactions with double-entry accounting',
    icon: 'book-open',
    category: 'Accounting',
    steps: [
      { selector: '[data-testid="journal-entries-page"]', title: 'Journal Entries', content: 'Journal entries are the foundation of your accounting. Every transaction is recorded here with balanced debits and credits.', position: 'bottom' },
      { selector: '[data-testid="new-entry-btn"]', title: 'Create New Entry', content: 'Click to create a new journal entry. Remember: debits must always equal credits!', position: 'left' },
      { selector: '[data-testid="help-panel-trigger"]', title: 'Debit & Credit Guide', content: 'Not sure about debits and credits? Click here for a color-coded reference table showing what each does to every account type.', position: 'left' },
    ]
  },

  chart_of_accounts: {
    id: 'chart_of_accounts',
    title: 'Chart of Accounts Tour',
    description: 'Your master list of financial accounts',
    icon: 'list-tree',
    category: 'Accounting',
    steps: [
      { selector: '[data-testid="chart-of-accounts"]', title: 'Chart of Accounts', content: 'This is your master list of all accounts — Assets, Liabilities, Equity, Revenue, and Expenses. Every transaction gets categorized into one of these.', position: 'bottom' },
      { selector: '[data-testid="add-account-btn"]', title: 'Add an Account', content: 'Click to add a new account. Choose the type, give it a code and name, and specify its normal balance side.', position: 'left' },
    ]
  },

  banking: {
    id: 'banking',
    title: 'Banking & Transactions Tour',
    description: 'Import, categorize, and reconcile bank transactions',
    icon: 'landmark',
    category: 'Banking',
    steps: [
      { selector: '[data-testid="banking-page"]', title: 'Banking Dashboard', content: 'This page shows your connected bank accounts and imported transactions. Categorize transactions to keep your books accurate.', position: 'bottom' },
      { selector: '[data-testid="connect-bank-btn"], [data-testid="banking-page"]', title: 'Connect a Bank', content: 'Click Connect Bank to securely link your accounts via Plaid. Transactions will flow in automatically — no more manual CSV uploads.', position: 'bottom' },
      { selector: '[data-testid="banking-page"]', title: 'Categorize Transactions', content: 'Uncategorized transactions show up at the top. Pick a Chart of Accounts category for each one — or let the AI Auto-Categorizer suggest them in bulk.', position: 'bottom' },
      { selector: '[data-testid="banking-page"]', title: 'Reconcile Monthly', content: 'At month-end, reconcile your books against your bank statement to catch missing or duplicate transactions. Aim for a clean reconciliation every month.', position: 'bottom' },
    ]
  },

  contacts: {
    id: 'contacts',
    title: 'Contacts & CRM Tour',
    description: 'Manage customers, vendors, and client relationships',
    icon: 'users',
    category: 'CRM',
    steps: [
      { selector: '[data-testid="contacts-page"]', title: 'Contacts CRM', content: 'Manage all your customers and vendors here. Track contact details, notes, custom fields, and financial history.', position: 'bottom' },
      { selector: '[data-testid="customers-tab"]', title: 'Customers Tab', content: 'View all your customers — the people and businesses that pay you. Each customer can have invoices, notes, and a portal login.', position: 'bottom' },
      { selector: '[data-testid="vendors-tab"]', title: 'Vendors Tab', content: 'View all your vendors — the businesses you pay. Track bills and payment history for each vendor.', position: 'bottom' },
    ]
  },

  reports: {
    id: 'reports',
    title: 'Financial Reports Tour',
    description: 'P&L, Balance Sheet, Cash Flow, Tax Mapping, and more',
    icon: 'bar-chart-2',
    category: 'Reports',
    steps: [
      { selector: '[data-testid="reports-hub"]', title: 'Reports Hub', content: 'Generate CPA-grade financial statements here. Profit & Loss, Balance Sheet, Cash Flow, Trial Balance, AR/AP Aging, Tax Liability, and the new Tax Mapping report.', position: 'bottom' },
      { selector: '[data-testid="reports-hub"]', title: 'Pick a Date Range', content: 'Every report lets you pick a custom date range. Common ranges (this month, last quarter, YTD, last year) are one click away.', position: 'bottom' },
      { selector: '[data-testid="reports-hub"]', title: 'Tax Mapping Report', content: 'NEW: see your ledger activity mapped to each line of your tax form (e.g. Schedule C). Includes a list of UNMAPPED accounts so you know what still needs categorizing.', position: 'bottom' },
      { selector: '[data-testid="reports-hub"]', title: 'Export & Narrate', content: 'Export any report to CSV or PDF. Click "AI Narrate" to get a plain-English summary of what the numbers actually mean for your business.', position: 'bottom' },
      { selector: '[data-testid="help-panel-trigger"]', title: 'Reports Help', content: 'Click the (?) on any report for explanations and tips for interpreting the data.', position: 'left' },
    ]
  },

  tasks: {
    id: 'tasks',
    title: 'Task Manager Tour',
    description: 'Organize work with Kanban boards and task lists',
    icon: 'clipboard-list',
    category: 'Productivity',
    steps: [
      { selector: '[data-testid="task-manager"]', title: 'Task Manager', content: 'Organize your team\'s work with a visual task board. Create tasks, assign them, set priorities, and track progress.', position: 'bottom' },
      { selector: '[data-testid="new-task-btn"]', title: 'Create a Task', content: 'Click to create a new task. Add a title, description, assign it to a team member, set a due date and priority.', position: 'left' },
      { selector: '[data-testid="task-settings-btn"]', title: 'Customize', content: 'Customize your task board — add custom statuses, priorities, categories, and assign team colors.', position: 'left' },
    ]
  },

  time_tracking: {
    id: 'time_tracking',
    title: 'Time Tracking Tour',
    description: 'Track billable hours and generate invoices from time',
    icon: 'timer',
    category: 'Productivity',
    steps: [
      { selector: '[data-testid="time-tracking"]', title: 'Time Tracking', content: 'Track billable and non-billable hours for your team. Use the live timer or add time manually.', position: 'bottom' },
    ]
  },

  ai_hub: {
    id: 'ai_hub',
    title: 'AI Chat Tour',
    description: 'Chat with Claude, GPT, Gemini, Llama, and 200+ other models',
    icon: 'sparkles',
    category: 'Intelligence',
    steps: [
      { selector: '[data-testid="ai-hub-page"]', title: 'AI Hub', content: 'Your unified AI workspace. Chat with 200+ models — Claude, GPT, Gemini, Llama, Mistral, and more — all from one place.', position: 'bottom' },
      { selector: '[data-testid="ai-hub-new-chat-btn"]', title: 'New Conversation', content: 'Start a fresh conversation. Previous chats are saved in the sidebar for easy reference.', position: 'right' },
      { selector: '[data-testid="ai-hub-model-picker"]', title: 'Pick a Model', content: 'Switch between models on the fly. Free-tier models are clearly labeled — paid models use your CountBucks AI credits.', position: 'bottom' },
      { selector: '[data-testid="ai-hub-composer-input"]', title: 'Compose Your Prompt', content: 'Type your message here. Attach images or PDFs for vision-capable models. Press Enter to send.', position: 'top' },
    ]
  },

  cfo_buck: {
    id: 'cfo_buck',
    title: 'CFO Buck Tour',
    description: 'AI-powered financial analysis and tax strategy',
    icon: 'brain',
    category: 'Intelligence',
    steps: [
      { selector: '[data-testid="cfo-buck-page"]', title: 'CFO Buck', content: 'Your AI Financial Officer. Get financial health analysis, tax strategies, and personalized advice powered by AI.', position: 'bottom' },
      { selector: '[data-testid="cfo-tabs"]', title: 'CFO Buck Tabs', content: 'Switch between Overview (health metrics), Tax Playbook (strategies), Tax Simulator (what-ifs), Ask CFO Buck (AI chat), and History (your saved conversations and plans).', position: 'bottom' },
      { selector: '[data-testid="tab-overview"]', title: 'Overview', content: 'See your financial health score, key metrics, and AI-generated insights at a glance.', position: 'bottom' },
      { selector: '[data-testid="tab-playbook"]', title: 'Tax Playbook', content: 'Browse curated tax-savings strategies filtered by your industry. Mark strategies as "used" or "interested" to build your action plan.', position: 'bottom' },
      { selector: '[data-testid="tab-chat"]', title: 'Ask CFO Buck', content: 'Have an open-ended financial question? Chat directly with CFO Buck for tailored advice.', position: 'bottom' },
      { selector: '[data-testid="analyze-tax-return-btn"]', title: 'Analyze a Tax Return', content: 'Upload last year\'s tax return and CFO Buck will scan it (via Statement Importer) then surface missed deductions and credits.', position: 'left' },
    ]
  },

  statement_importer: {
    id: 'statement_importer',
    title: 'Statement Importer Tour',
    description: 'OCR for bank statements, receipts, invoices, and tax returns',
    icon: 'scan-line',
    category: 'Documents',
    steps: [
      { selector: '[data-testid="statement-importer-page"]', title: 'Statement Importer', content: 'Upload bank statements, receipts, invoices, or tax returns. AI extracts the data so you don\'t have to type it in.', position: 'bottom' },
      { selector: '[data-testid="scan-credits-bar"]', title: 'Scan Credits', content: 'Each scan uses one credit. Your plan tier determines how many credits you get per month — upgrade for more.', position: 'bottom' },
      { selector: '[data-testid="doc-type-bank_statement"]', title: 'Pick a Document Type', content: 'Tell us what you\'re uploading — bank statement, receipt, invoice, or tax return. The extractor uses tailored prompts for each type.', position: 'bottom' },
      { selector: '[data-testid="file-drop-zone"]', title: 'Drop a File', content: 'Drag-and-drop a PDF or image, or click to browse. Multi-page PDFs are scanned page-by-page.', position: 'top' },
    ]
  },

  tax_mapper: {
    id: 'tax_mapper',
    title: 'Tax Mapper Tour',
    description: 'Map your chart of accounts to tax form lines (with AI)',
    icon: 'map',
    category: 'Accounting',
    steps: [
      { selector: '[data-testid="tax-mapper-page"]', title: 'Tax Mapper', content: 'Map each Chart of Accounts entry to a specific line on your tax form (Schedule C, Form 1120, 1120-S, or 1065). Saves hours at tax time.', position: 'bottom' },
      { selector: '[data-testid="tax-mapper-view-report-btn"]', title: 'See Your Tax Mapping Report', content: 'Click here to jump to the Tax Mapping report — a full breakdown of which accounts feed which tax lines, with section subtotals and a list of any unmapped accounts.', position: 'bottom' },
      { selector: '[data-testid="form-type-schedule_c"]', title: 'Pick Your Form', content: 'Choose your tax form. The Mapper shows the relevant line items for that form so you only see what matters.', position: 'bottom' },
      { selector: '[data-testid="tax-mapper-ai-suggest-btn"]', title: 'AI Auto-Suggest ✨', content: 'NEW: let Claude Sonnet 4.5 propose mappings for every unmapped account in one batch — typically 100% confidence on obvious matches. Review and apply in seconds.', position: 'bottom' },
      { selector: '[data-testid="mapper-search"]', title: 'Filter Accounts', content: 'Search or filter accounts to focus on what still needs mapping. Click any account to assign its tax line.', position: 'bottom' },
      { selector: '[data-testid="save-mappings-btn"]', title: 'Save Your Mappings', content: 'Save your mappings — they\'ll be remembered so next year\'s tax prep is faster.', position: 'left' },
    ]
  },

  settings: {
    id: 'settings',
    title: 'Settings Tour',
    description: 'Workspace, team, integrations, billing, and your website',
    icon: 'settings',
    category: 'Admin',
    steps: [
      { selector: '[data-testid="settings"]', title: 'Settings', content: 'Configure your workspace, manage team members, set up integrations, customize invoice templates, and even spin up your public website.', position: 'bottom' },
      { selector: '[data-testid="tab-workspace"]', title: 'Workspace Settings', content: 'Set your company name, address, currency, fiscal year, and default payment terms.', position: 'bottom' },
      { selector: '[data-testid="tab-team"]', title: 'Team Management', content: 'Add team members, assign roles (owner / admin / accountant / staff / client portal), and upload profile photos.', position: 'bottom' },
      { selector: '[data-testid="tab-integrations"]', title: 'Integrations', content: 'Connect your bank via Plaid, set up Stripe for invoice payments (incl. Stripe Connect for portal clients), and configure email with Resend.', position: 'bottom' },
      { selector: '[data-testid="tab-website"], [data-testid="tab-sites"], [data-testid="settings"]', title: 'Your Website', content: 'NEW: build a public 1-page website for your business right inside CountBucks — describe your vision, AI generates the copy & design, you can connect a custom domain. Take the dedicated Website tour for details.', position: 'bottom' },
      { selector: '[data-testid="tab-billing"], [data-testid="settings"]', title: 'Billing & Plan', content: 'See your current plan, AI credit balance, and Universal Key top-up controls. Upgrade anytime to unlock more AI features.', position: 'bottom' },
    ]
  },

  university: {
    id: 'university',
    title: 'University Tour',
    description: 'Navigate the learning platform and earn certification',
    icon: 'graduation-cap',
    category: 'Learning',
    steps: [
      { selector: '[data-testid="university-page"]', title: 'CountBucks University', content: 'Your learning hub! Complete 8 modules covering accounting, taxes, business management, and more.', position: 'bottom' },
      { selector: '[data-testid="module-card-accounting_foundations"]', title: 'Module Cards', content: 'Click any module to see its lessons. Complete all lessons to unlock the module quiz.', position: 'bottom' },
      { selector: '[data-testid="final-exam-card"]', title: 'Final Exam', content: 'After completing all 8 modules, take the comprehensive final exam to earn your CountBucks certification!', position: 'top' },
    ]
  },

  recurring: {
    id: 'recurring',
    title: 'Recurring Entries Tour',
    description: 'Automate repeating journal entries and transactions',
    icon: 'repeat',
    category: 'Accounting',
    steps: [
      { selector: '[data-testid="recurring-entries"]', title: 'Recurring Entries', content: 'Set up journal entries that repeat automatically — perfect for rent, subscriptions, depreciation, and other regular transactions.', position: 'bottom' },
      { selector: '[data-testid="new-recurring-btn"]', title: 'Create a Recurring Entry', content: 'Click to set up a new recurring entry — choose the cadence (daily, weekly, monthly) and the entry template.', position: 'left' },
    ]
  },

  document_inbox: {
    id: 'document_inbox',
    title: 'Document Inbox Tour',
    description: 'Upload, scan, and organize business documents',
    icon: 'inbox',
    category: 'Documents',
    steps: [
      { selector: '[data-testid="document-inbox"]', title: 'Document Inbox', content: 'Upload receipts, bills, statements, and other documents. OCR scanning extracts data automatically so you can create entries from uploads.', position: 'bottom' },
      { selector: '[data-testid="upload-btn"]', title: 'Upload a Document', content: 'Click to upload a file. Once processed, you can attach it to invoices, bills, or journal entries for an audit-ready paper trail.', position: 'left' },
    ]
  },

  achievements: {
    id: 'achievements',
    title: 'Achievements Tour',
    description: 'Earn badges as you master CountBucks features',
    icon: 'trophy',
    category: 'Learning',
    steps: [
      { selector: '[data-testid="achievements-page"]', title: 'Achievements', content: 'Earn badges as you hit milestones — posting journal entries, sending invoices, completing University modules, and more. There are 18 badges across 5 categories.', position: 'bottom' },
      { selector: '[data-testid="badge-toggle"]', title: 'Disable Badges', content: 'Don\'t want badge notifications? Toggle this switch to turn them off. You can re-enable anytime.', position: 'left' },
      { selector: '[data-testid="badge-scholar"]', title: 'Earned Badges', content: 'Earned badges show a colored icon and the date you earned them. Locked badges appear grayed out with a lock icon.', position: 'bottom' },
    ]
  },

  leaderboard: {
    id: 'leaderboard',
    title: 'Leaderboard Tour',
    description: 'See how your team ranks on badges and progress',
    icon: 'medal',
    category: 'Learning',
    steps: [
      { selector: '[data-testid="leaderboard-page"]', title: 'Team Leaderboard', content: 'See how everyone on your team is progressing through CountBucks. Rankings are based on badges earned, University modules completed, and certification status.', position: 'bottom' },
    ]
  },

  website: {
    id: 'website',
    title: 'Website Builder Tour',
    description: 'Build, edit, and publish your business\'s public website',
    icon: 'globe',
    category: 'Marketing',
    steps: [
      { selector: '[data-testid="site-editor"], [data-testid="settings"]', title: 'Your Public Website', content: 'CountBucks comes with a built-in 1-page website builder. Publish at /s/<your-slug> or connect your own custom domain — everything from one screen.', position: 'bottom' },
      { selector: '[data-testid="site-ai-vision"], [data-testid="site-editor"]', title: 'Describe Your Vision', content: 'Click "Describe your vision" and tell the AI what your business does. Claude Sonnet 4.5 (or any model you pick) will draft the entire site — hero, services, about, contact form — in seconds.', position: 'bottom' },
      { selector: '[data-testid="site-ai-chat-open"], [data-testid="site-editor"]', title: 'Chat with AI to Edit', content: 'Want changes? Open "Chat with AI to edit" and just say what you want — "make the hero shorter", "switch to a bold theme with dark purple", "add a FAQ section". The AI patches the site live and you Apply when you\'re happy.', position: 'bottom' },
      { selector: '[data-testid="site-ai-model-picker"], [data-testid="site-editor"]', title: 'Pick a Model', content: 'Choose from Claude Sonnet 4.5, Claude Opus 4.5, GPT-5.2, GPT-4o, or Gemini 2.5 Pro. Your choice is remembered across sessions.', position: 'bottom' },
      { selector: '[data-testid="site-section-trust"], [data-testid="site-editor"]', title: 'Section Toggles', content: 'Turn any section on or off — Hero, Trust badges, Stats, About, Services, Testimonials, FAQ, Contact info, Contact form, Footer. Every section is editable inline too.', position: 'bottom' },
      { selector: '[data-testid="custom-domain-card"], [data-testid="site-editor"]', title: 'Connect a Custom Domain', content: 'Want yoursite.com instead of /s/your-slug? Enter your domain, add the DNS records we generate, click Verify. Once verified, Cloudflare provisions SSL within 24 hours.', position: 'bottom' },
      { selector: '[data-testid="site-publish-toggle"], [data-testid="site-editor"]', title: 'Publish & Share', content: 'Toggle Publish to go live. Your public URL appears with one-click Copy and Open buttons — share it on social, business cards, or email signatures.', position: 'bottom' },
    ]
  },

  ai_memory: {
    id: 'ai_memory',
    title: 'AI Memory Tour',
    description: 'Teach the AI durable facts about your business',
    icon: 'brain',
    category: 'Intelligence',
    steps: [
      { selector: '[data-testid="memory-hub-page"]', title: 'AI Memory', content: 'Memory is what makes the AI feel like it actually knows your business. Add durable facts — goals, pricing, brand voice, cash-flow habits — and every AI chat, email, and brief grounds itself in them.', position: 'bottom' },
      { selector: '[data-testid="memory-sync-btn"], [data-testid="memory-hub-page"]', title: 'Sync from Chat', content: 'Already had useful chats in AI Studio? Click "Sync from chat" and we\'ll scan recent threads and propose durable facts for you to approve in batch — no manual retyping.', position: 'bottom' },
      { selector: '[data-testid="memory-hub-page"]', title: 'Smart Categories', content: 'There are 11 categories: Goals, Company, Team & Roles, Products & Pricing, Cash Flow Habits, Routines & Cadence, Industry & Market, Brand Voice, Tax, Preferences, and Other. Each tells the AI something specific.', position: 'bottom' },
      { selector: '[data-testid="memory-empty-goals"], [data-testid="memory-hub-page"]', title: 'Starter Prompts', content: 'Empty category? Click any "💡 Try adding…" chip — it prefills the input with a real example you can edit before saving. Zero blank-page anxiety.', position: 'bottom' },
      { selector: '[data-testid="memory-hub-page"]', title: 'Quick-add Bar', content: 'At the top there\'s a one-line quick-add. Pick a category, type the fact, hit Enter. Use the /remember <fact> shortcut inside any AI chat to add memories without leaving the conversation.', position: 'bottom' },
    ]
  },

  messages: {
    id: 'messages',
    title: 'Client Messages Tour',
    description: 'Communicate directly with your portal clients',
    icon: 'message-square',
    category: 'CRM',
    steps: [
      { selector: '[data-testid="messages-page"], [data-testid="settings"]', title: 'Messages Center', content: 'Send and receive messages with any client who has portal access. Everything stays inside CountBucks — no more email-tag for "where\'s that invoice?".', position: 'bottom' },
      { selector: '[data-testid="messages-page"], [data-testid="settings"]', title: 'Thread per Client', content: 'Each client gets a persistent thread — search, scroll, attach documents. Clients see the same conversation from their portal so nothing falls through the cracks.', position: 'bottom' },
      { selector: '[data-testid="messages-page"], [data-testid="settings"]', title: 'Email Notifications', content: 'Clients get an email notification whenever you reply — and you get one when they do. Reset their portal password right from the message thread if they\'re locked out.', position: 'bottom' },
    ]
  },

  // ─── NEW (Feb 30 2026) ─ Tours added to cover every nav item ─────────

  business_dashboard: {
    id: 'business_dashboard',
    title: 'Business Dashboard Tour',
    description: 'Your customizable owner KPIs — drag, drop, drill in',
    icon: 'layout-dashboard',
    category: 'Overview',
    steps: [
      { selector: '[data-testid="dash-range-toggle"]', title: 'Pick your time range', content: 'Toggle between 7-day, 30-day, 90-day, and YTD views. Every card on the dashboard re-computes against the range you pick.', position: 'bottom' },
      { selector: '[data-testid="dash-customize-toggle"]', title: 'Customize layout', content: 'Click Customize to drag-and-drop cards into any order, hide ones you don\'t use, and save the layout per workspace. Your layout is remembered the next time you visit.', position: 'bottom' },
      { selector: '[data-testid="dash-funnel-paid-link"], [data-testid="dash-card-0"]', title: 'Sparklines + drill-downs', content: 'Each card has a mini-sparkline showing the trend over your selected range. Click any metric (paid / unpaid invoices, expenses, AI suggestions) to drill straight into the filtered list.', position: 'bottom' },
      { selector: '[data-testid="dash-ai-open-buck"]', title: 'Ask the AI from here', content: 'Suggested business questions appear at the bottom of the dashboard. Click one to open CFO Buck with the question pre-loaded — no copy-pasting.', position: 'top' },
    ]
  },

  switch_from_quickbooks: {
    id: 'switch_from_quickbooks',
    title: 'Switch from QuickBooks Tour',
    description: 'Pick your migration path: Fresh Start, Match my QB, or Full replay',
    icon: 'list-tree',
    category: 'Migration',
    steps: [
      { selector: '[data-testid="migration-hub"]', title: 'Three doors, your choice', content: 'Switching is graded by effort: Fresh Start (3 min, no exports), Match my QB Reports (10 min, upload P&L + Balance Sheet), or Full Historical Replay (20 min, CPA-led).', position: 'bottom' },
      { selector: '[data-testid="door-fresh"]', title: 'Door 1 — Fresh Start', content: 'Recommended for most small businesses. Pick a date → connect your bank → done. No exports, no spreadsheets. Historical periods stay in QB for reference.', position: 'bottom' },
      { selector: '[data-testid="door-pnl_bs"]', title: 'Door 2 — Match my reports', content: 'Most common. We walk you click-by-click through exporting your QB P&L and Balance Sheet (with exact menu paths + default filenames), then we post your opening balances as one balanced JE.', position: 'bottom' },
      { selector: '[data-testid="door-full"]', title: 'Door 3 — Full replay', content: 'For clean year-end audits or CPA-led migrations. Trial Balance + Transaction Detail import for line-by-line history.', position: 'bottom' },
      { selector: '[data-testid="toggle-other-tools"]', title: 'Other tools', content: 'Need a cost-savings calculator or the legacy manual checklist? They live behind this toggle so they don\'t clutter the main flow.', position: 'top' },
    ]
  },

  all_conversations: {
    id: 'all_conversations',
    title: 'All Conversations Tour',
    description: 'Every chat & saved plan across CountBucks in one place',
    icon: 'message-square',
    category: 'AI & Intelligence',
    steps: [
      { selector: '[data-testid="all-conversations-page"]', title: 'One inbox for every chat', content: 'AI Studio, CFO Buck, Quick Chat, Tax Plans — every conversation across CountBucks lands here. Search, tag, filter, and pin from one screen.', position: 'bottom' },
      { selector: '[data-testid="all-conversations-search"]', title: 'Full-text search', content: 'Type any keyword and we search across every conversation\'s title AND body. Great for "what did the AI say about my Q3 cash flow?" type questions.', position: 'bottom' },
      { selector: '[data-testid="folders-row"], [data-testid="all-conversations-page"]', title: 'Save as folder', content: 'Combine filter + tag + search into a reusable folder. "Tax conversations from 2026" or "All open client questions" — saved folders appear as one-click chips.', position: 'bottom' },
      { selector: '[data-testid="tag-filter-row"], [data-testid="all-conversations-page"]', title: 'Tags', content: 'Tag any chat to surface a custom view later. Tags carry across all chat surfaces, so you can build your own taxonomy.', position: 'bottom' },
      { selector: '[data-testid="bulk-action-bar"], [data-testid="all-conversations-page"]', title: 'Bulk actions', content: 'Select multiple chats to pin, archive, delete, or re-tag at once. The bulk action bar appears as soon as you check a row.', position: 'top' },
    ]
  },

  documents_hub: {
    id: 'documents_hub',
    title: 'Documents Hub Tour',
    description: 'Your single source for contracts, receipts, statements, and more',
    icon: 'file-text',
    category: 'Banking & Documents',
    steps: [
      { selector: '[data-testid="documents-hub"]', title: 'One home for every file', content: 'Documents Hub is the canonical filer for everything you upload — bills, invoices, signed contracts, statements, tax returns. Files attached to other records also surface here.', position: 'bottom' },
      { selector: '[data-testid="documents-hub"]', title: 'Drag-drop anywhere', content: 'Drop PDFs, images, or whole folders onto the page. The Hub OCRs them automatically and proposes a destination (Bill, Invoice, Tax Return, etc.).', position: 'bottom' },
      { selector: '[data-testid="documents-hub"]', title: 'Smart folders & permissions', content: 'Create smart folders (auto-collect by tag/search) and grant fine-grained per-member permissions. Great for sharing only the right files with a portal client or staff member.', position: 'bottom' },
      { selector: '[data-testid="documents-hub"]', title: 'Bulk operations + Undo', content: 'Move, tag, delete in bulk — every destructive action shows an Undo toast for 10 seconds so a fat-finger never loses data.', position: 'top' },
    ]
  },

  signatures: {
    id: 'signatures',
    title: 'Signatures Tour',
    description: 'Send PDFs for legally-binding electronic signatures',
    icon: 'file-text',
    category: 'Banking & Documents',
    steps: [
      { selector: '[data-testid="signatures-hub"]', title: 'E-signature for every doc', content: 'Send any PDF for signature — engagement letters, contracts, tax authorizations. Signed copies auto-file into Documents Hub with audit trail attached.', position: 'bottom' },
      { selector: '[data-testid="tab-new"]', title: '+ New Request', content: 'Click + New Request to upload a PDF, place signature/date/initial fields, pick recipients, and send. Recipients sign right in the browser — no account needed.', position: 'bottom' },
      { selector: '[data-testid="tab-templates"]', title: 'Templates', content: 'Save common documents (NDAs, engagement letters, W-9 requests) as templates with the signature fields pre-positioned. One click to re-send to a new client.', position: 'bottom' },
      { selector: '[data-testid="tab-requests"]', title: 'Track requests', content: 'See every outstanding request in one list — who\'s signed, who hasn\'t, when it was sent. Click Resend for one-tap nudges.', position: 'bottom' },
    ]
  },

  client_inbox: {
    id: 'client_inbox',
    title: 'Client Inbox Tour',
    description: 'Unified inbox: email replies + portal messages in one thread',
    icon: 'inbox',
    category: 'CRM & Productivity',
    steps: [
      { selector: '[data-testid="inbox-page"]', title: 'Every client conversation', content: 'Replies to your invoice emails AND messages from clients in the Client Portal both land here — one timeline per client, no more switching tools.', position: 'bottom' },
      { selector: '[data-testid="inbox-page"]', title: 'Reply inline', content: 'Reply right from the thread. Your reply goes via email if they reached you that way, or via portal message if they messaged through the portal — the channel is preserved.', position: 'bottom' },
      { selector: '[data-testid="inbox-page"]', title: 'Sidebar badge', content: 'The Client Inbox sidebar item shows an unread count whenever new replies arrive, so nothing slips through the cracks.', position: 'bottom' },
    ]
  },

  prompt_launchpad: {
    id: 'prompt_launchpad',
    title: 'Prompt Launchpad Tour',
    description: '64+ ready-to-run AI prompts grouped by business hub',
    icon: 'sparkles',
    category: 'AI & Intelligence',
    steps: [
      { selector: '[data-testid="ai-command-center"]', title: 'AI prompt library', content: 'Eight business hubs (Finance, CRM, Marketing, Legal, HR, Ops, Productivity, Knowledge) — each with curated, business-focused prompts ready to drop into a new chat.', position: 'bottom' },
      { selector: '[data-testid="hub-card-finance"], [data-testid="ai-command-center"]', title: 'Pick a hub', content: 'Click any hub card to browse its prompts. Each prompt is one click to launch — pre-loaded into AI Studio with the right system message and model defaults.', position: 'bottom' },
      { selector: '[data-testid="open-ai-studio-btn"]', title: 'Or start from scratch', content: 'Don\'t see what you want? Click "Open AI Studio" to start a blank chat with any of the 200+ models.', position: 'bottom' },
      { selector: '[data-testid="recent-thread-undefined"], [data-testid="ai-command-center"]', title: 'Pick up where you left off', content: 'Your recent chats appear at the bottom — one click resumes any conversation.', position: 'top' },
    ]
  },

  auto_categorizer: {
    id: 'auto_categorizer',
    title: 'Auto-Categorizer Tour',
    description: 'Let AI sort your transactions into the right accounts',
    icon: 'sparkles',
    category: 'AI & Intelligence',
    steps: [
      { selector: '[data-testid="ai-buck"]', title: 'AI categorization queue', content: 'AI Buck reviews every uncategorized bank transaction and proposes a category + memo. You review, accept, or override — then the JE posts in one click.', position: 'bottom' },
      { selector: '[data-testid="generate-suggestions-btn"]', title: 'Generate suggestions', content: 'Click Generate Suggestions to run AI on every pending transaction at once. Suggestions appear with a confidence score so you know which ones to trust most.', position: 'bottom' },
      { selector: '[data-testid="tab-rules"]', title: 'Recurring rules', content: 'When you accept the same suggestion repeatedly (e.g. all "STRIPE TRANSFER" rows → "Sales"), Auto-Categorizer offers to save it as a rule. Future matches auto-post.', position: 'bottom' },
      { selector: '[data-testid="tab-settings"]', title: 'Settings', content: 'Choose which bank accounts AI Buck watches, set confidence thresholds for auto-posting, and pick your model (Claude / GPT / Gemini).', position: 'bottom' },
    ]
  },

  smart_reconcile: {
    id: 'smart_reconcile',
    title: 'Smart Reconcile Tour',
    description: 'AI-assisted bank reconciliation in minutes',
    icon: 'landmark',
    category: 'Banking & Documents',
    steps: [
      { selector: '[data-testid="reconcile-buck-page"]', title: 'Statement matching, fast', content: 'Upload your bank statement (PDF or CSV) — AI matches each statement line to a transaction in your books, flags discrepancies, and lets you post adjustments inline.', position: 'bottom' },
      { selector: '[data-testid="reconcile-buck-page"]', title: 'AI auto-match', content: 'For 80%+ of statement lines we find an exact match instantly. The rest get fuzzy-match candidates ranked by confidence — pick one, or post a new JE on the fly.', position: 'bottom' },
      { selector: '[data-testid="reconcile-buck-page"]', title: 'Reconcile A/R + A/P too', content: 'The Reconcile skill also matches deposits to open invoices (A/R) and withdrawals to open bills (A/P) — close-out invoices and bills directly from the matching view.', position: 'bottom' },
    ]
  },

  invoice_ocr: {
    id: 'invoice_ocr',
    title: 'Document Scanner Tour',
    description: 'Drop ANY document — AI Buck reads it and lights up Smart Actions',
    icon: 'scan-line',
    category: 'Banking & Documents',
    steps: [
      { selector: '[data-testid="invoice-ocr-page"]', title: 'Single-doc OCR', content: 'Drop one document at a time here — invoice, bill, W-9, 1040, contract, statement. AI reads it and lights up the relevant Smart Actions for that doc type.', position: 'bottom' },
      { selector: '[data-testid="ocr-scanning-banner"], [data-testid="invoice-ocr-page"]', title: 'Real-time progress', content: 'A banner shows OCR scan progress — multi-page PDFs are streamed page-by-page so you can start reviewing before the whole file is done.', position: 'bottom' },
      { selector: '[data-testid="routing-suggestion-banner"], [data-testid="invoice-ocr-page"]', title: 'Smart routing', content: 'AI suggests where this doc belongs (Bill, Invoice, Statement Importer, Tax Return). One click confirms — fields auto-populate downstream.', position: 'bottom' },
      { selector: '[data-testid="create-draft-btn"], [data-testid="invoice-ocr-page"]', title: 'Create draft entry', content: 'Once review is done, "Create Draft" opens a pre-filled Bill/Invoice ready for your final touches. The original PDF stays attached for audit trail.', position: 'top' },
    ]
  },

  loan_qualification: {
    id: 'loan_qualification',
    title: 'Loan Qualification Tour',
    description: 'DSCR analysis and cash-flow counseling for loan applications',
    icon: 'bar-chart-2',
    category: 'Analysis',
    steps: [
      { selector: '[data-testid="dscr-gauge"]', title: 'Your DSCR at a glance', content: 'Debt Service Coverage Ratio — the single number lenders use to qualify you. We pull it live from your books so it always reflects today\'s state.', position: 'bottom' },
      { selector: '[data-testid="dscr-manual-calculator"]', title: 'What-if calculator', content: 'Want to model a new loan or a revenue scenario? Plug numbers into the manual calculator to see how your DSCR shifts before you apply.', position: 'bottom' },
      { selector: '[data-testid="period-toggle"]', title: 'Annual vs monthly', content: 'Toggle between monthly and annual figures depending on what your lender wants. The DSCR formula stays the same — the inputs just rescale.', position: 'bottom' },
    ]
  },

  general_ledger: {
    id: 'general_ledger',
    title: 'General Ledger Tour',
    description: 'See every transaction posted to every account, with running balances',
    icon: 'book-open',
    category: 'Accounting',
    steps: [
      { selector: '[data-testid="general-ledger"]', title: 'The full ledger', content: 'Every posted journal line, grouped by account, in date order with a running balance. This is the audit-grade view your CPA wants to see.', position: 'bottom' },
      { selector: '[data-testid="account-select"]', title: 'Pick an account', content: 'Use the selector to focus on one account at a time. Date range and running balance recompute against your selection.', position: 'bottom' },
      { selector: '[data-testid="ending-balance"], [data-testid="general-ledger"]', title: 'Ending balance', content: 'The ending balance for the selected account and date range is shown prominently — perfect for reconciling against statements or tax-return line items.', position: 'bottom' },
      { selector: '[data-testid="general-ledger"]', title: 'Click any line', content: 'Click any ledger row to open the source journal entry. Source documents (uploaded receipts, bills) are linked on the JE so you can drill all the way to the paper trail.', position: 'top' },
    ]
  },

  audit_log: {
    id: 'audit_log',
    title: 'Audit Log Tour',
    description: 'Every change to your books — who, what, when',
    icon: 'inbox',
    category: 'Admin & Learning',
    steps: [
      { selector: '[data-testid="audit-log-page"]', title: 'Every change tracked', content: 'JE creates, edits, deletes, voids, member additions, settings changes — every write to the database is logged with the user, timestamp, and before/after state.', position: 'bottom' },
      { selector: '[data-testid="audit-search"]', title: 'Search the trail', content: 'Search by user name, account, entry number, or any keyword in the diff. Great for "who touched this entry last Tuesday?".', position: 'bottom' },
      { selector: '[data-testid="action-filter"]', title: 'Filter by action', content: 'Narrow to specific actions: create, update, delete, void, reverse, login, role-change. Combine with resource filter for "every JE delete this month".', position: 'bottom' },
      { selector: '[data-testid="resource-filter"]', title: 'Filter by resource type', content: 'Limit to one resource: Journal Entries, Invoices, Bills, Settings, Team. Audit ready in seconds.', position: 'bottom' },
    ]
  },
};

// Map tour IDs to their app routes. Used by both GuidedTours hub and TourLauncher
// so a tour only auto-starts once we're actually on the matching page.
export const TOUR_ROUTES = {
  dashboard: '/dashboard',
  invoices: '/invoices',
  bills: '/bills',
  journal_entries: '/journal-entries',
  chart_of_accounts: '/accounts',
  banking: '/banking',
  contacts: '/contacts',
  reports: '/reports',
  tasks: '/tasks',
  time_tracking: '/time-tracking',
  ai_hub: '/ai-hub',
  cfo_buck: '/cfo-buck',
  statement_importer: '/statement-importer',
  tax_mapper: '/tax-mapper',
  settings: '/settings',
  university: '/university',
  recurring: '/recurring-entries',
  document_inbox: '/document-inbox',
  achievements: '/achievements',
  leaderboard: '/leaderboard',
  website: '/settings',
  ai_memory: '/memory',
  messages: '/messages',
  // Feb 30 2026 — coverage gap close-out
  business_dashboard:     '/business-dashboard',
  switch_from_quickbooks: '/switch-from-quickbooks',
  all_conversations:      '/all-conversations',
  documents_hub:          '/documents',
  signatures:             '/signatures',
  client_inbox:           '/inbox',
  prompt_launchpad:       '/ai-command',
  auto_categorizer:       '/ai-buck',
  smart_reconcile:        '/reconcile-buck',
  invoice_ocr:            '/invoice-ocr',
  loan_qualification:     '/loan-qualification',
  general_ledger:         '/ledger',
  audit_log:              '/audit',
};

// Grouped by category for the hub page
export const tourCategories = [
  {
    name: 'Overview',
    tours: ['dashboard', 'business_dashboard'],
  },
  {
    name: 'Accounting',
    tours: ['journal_entries', 'chart_of_accounts', 'recurring', 'tax_mapper', 'general_ledger'],
  },
  {
    name: 'Receivables & Payables',
    tours: ['invoices', 'bills'],
  },
  {
    name: 'Banking & Documents',
    tours: ['banking', 'documents_hub', 'invoice_ocr', 'statement_importer', 'smart_reconcile', 'signatures', 'document_inbox'],
  },
  {
    name: 'AI & Intelligence',
    tours: ['ai_hub', 'ai_memory', 'cfo_buck', 'auto_categorizer', 'prompt_launchpad', 'all_conversations', 'reports'],
  },
  {
    name: 'Analysis',
    tours: ['loan_qualification'],
  },
  {
    name: 'Migration',
    tours: ['switch_from_quickbooks'],
  },
  {
    name: 'Marketing & Public Site',
    tours: ['website'],
  },
  {
    name: 'CRM & Productivity',
    tours: ['contacts', 'messages', 'client_inbox', 'tasks', 'time_tracking'],
  },
  {
    name: 'Admin & Learning',
    tours: ['settings', 'audit_log', 'university', 'achievements', 'leaderboard'],
  },
];
